import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import { ListingConfigProps } from 'layout/Listing/config';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import EventFilters from 'layout/Listing/variants/events/components/EventFilters';
import SearchBar from 'layout/Listing/variants/events/components/SearchBar';
import useEventFilterSearchParam from 'layout/Listing/variants/events/hooks/useEventFilterSearchParam';
import useFilterEvents from 'layout/Listing/variants/events/hooks/useFilteredEvents';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';

import events from 'data/events.json';

const PAGE_SIZE = 12;

type EventItem = { data: EventResource };

const EventsListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<EventItem>(rawLatestData);

  const localData = toArray<EventItem>(events);

  const eventsList: EventItem[] = hasArrayValues(latestData) ? mergeListings(latestData, localData) : localData;

  if (!hasArrayValues(eventsList)) {
    console.warn('[EventsListing] No items to render – check builderState/listingData and events.json');
  }

  useEventFilterSearchParam();

  const filteredEvents = useFilterEvents(eventsList).sort(
    (a, b) => new Date(b.data?.sortableEventDate || 0).getTime() - new Date(a.data?.sortableEventDate || 0).getTime(),
  );

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredEvents,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/events');

  useForceReloadOnBackToResources();

  return (
    <Section {...sectionProps} darkMode={false}>
      <ScrollFadeSection delay={0.1} containPaint={false}>
        <div id="listing-section" className="flex flex-col gap-8">
          <div className="grid w-full grid-cols-1 justify-between gap-4 lg:grid-cols-3">
            <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
              {headingTitle || 'All Events & Webinars'}
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end lg:col-span-2">
              <EventFilters />
              <SearchBar placeholder={searchPlaceholder} />
            </div>
          </div>
          <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />
          {totalItems > PAGE_SIZE && (
            <Pagination
              count={totalItems}
              pageSize={PAGE_SIZE}
              setPage={detail => handlePageChange(detail.page)}
              currentPage={currentPage}
            />
          )}
        </div>
      </ScrollFadeSection>
    </Section>
  );
};

export default EventsListing;
